import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {memo, useMemo, useState} from 'react';

import {IconPositioningContextProvider} from '../../state/issues/IconPositioningContext';
import {prettyXAxisLabel} from '../../util/plotHelpers/axis';
import {
  getPlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {useResizeObserverDebounced} from '../../util/useResizeObserverDebounced';
import {PanelTitleView} from '../PanelTitle/PanelTitleView';
import {EMPTY_ANNOTATION_HOVER, EMPTY_ANNOTATIONS} from '../vis/annotations';
import {
  presentChartSeries,
  presentNumericLines,
  resolveNumericSeries,
} from '../vis/chartSeries';
import {createLocalGraphInteraction} from '../vis/interaction';
import {LinePlot} from '../vis/LinePlot';
import {BarChart} from './BarChart';
import {DelayedMetricsNotice} from './DelayedMetricsNotice';
import {getErrorMessage} from './getGraphError';
import {partitionLines} from './graphLines';
import {
  DEFAULT_GRAPH_LEGEND_POSITION,
  type GraphNotices,
  type GraphRenderProps,
} from './graphRenderProps';
import {IssueMarkerList} from './IssueMarkerList';
import {getIssuesWrapperStatus} from './issueMarkers';
import {PlotLayoutView} from './Layout';
import {LinePlotLegendView} from './LinePlotLegendView';
import {
  LinePlotLegendWarningView,
  type LinePlotLegendWarningViewProps,
} from './LinePlotLegendWarningView';
import {
  type GraphRendererSettings,
  LinePlotSettingsProvider,
} from './LinePlotSettingsContext';
import {MissingRunBanner} from './MissingRunBanner';
import {SamplingWarningView} from './SamplingWarningView';
import {getIsLegendShown} from './useLegendVisibility';
import {useLinesForDisplay} from './useLinesForDisplay';
import {
  linesHaveRenderableData,
  useRegexMetricSettlingLoading,
} from './useRegexMetricSettlingLoading';
import {isBarPlot} from './utils/checkTypes';

const expandStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const SAMPLING_WARNING_STYLE: React.CSSProperties = {zIndex: 1};

const GraphComp: React.FC<GraphRenderProps> = props => {
  const {
    annotations,
    appearance,
    axis,
    errors,
    fallbacks,
    interaction: suppliedInteraction,
    layout,
    legend,
    loading,
    notices,
    panel,
    rendering,
    series,
    seriesStylesById,
    seriesMetadataById,
    title,
    tooltip,
    zoom,
  } = props;
  const issueLines = annotations?.items ?? EMPTY_ANNOTATIONS;
  // Keep each mounted notice dependent only on the fields it displays.
  const limitNotices: LinePlotLegendWarningViewProps['notices'] = {
    displayRowLimitPerRunset: notices?.displayRowLimitPerRunset,
    fetchedRunCount: notices?.fetchedRunCount,
    groupedIncludedRunCount: notices?.groupedIncludedRunCount,
    groupInputRunLimitPerRunset: notices?.groupInputRunLimitPerRunset,
    groupingDepth: notices?.groupingDepth,
    hasGroupedRunsets: notices?.hasGroupedRunsets,
    hasSubsampledGroups: notices?.hasSubsampledGroups,
    maxFetchedRunsPerRunset: notices?.maxFetchedRunsPerRunset,
    metricCount: notices?.metricCount,
    runsetCount: notices?.runsetCount,
    // Show guidance for changing chart limits unless the caller disables it.
    // The app adapter disables this help for read-only charts.
    showLimitHelp: notices?.showLimitHelp ?? true,
  };
  const samplingNotices: GraphNotices = {
    isSamplingFallback: notices?.isSamplingFallback,
    samplingWarnings: notices?.samplingWarnings,
  };
  const delayedNotices: GraphNotices = {
    onDelayedNoticeShown: notices?.onDelayedNoticeShown,
    onLoadAnyway: notices?.onLoadAnyway,
  };

  // state
  const [isHovered, setIsHovered] = useState(false);
  const [localInteraction] = useState(createLocalGraphInteraction);
  const interaction = suppliedInteraction ?? localInteraction;
  const annotationHoverSource = {
    get: annotations?.readHoveredId ?? EMPTY_ANNOTATION_HOVER.get,
    subscribe:
      annotations?.subscribeToHover ?? EMPTY_ANNOTATION_HOVER.subscribe,
  };
  const legendFeature =
    legend == null
      ? undefined
      : {
          ...legend,
          onHighlight: interaction.events.onSeriesHover,
        };

  const {
    isResizing,
    height = 1,
    ref: elementRef,
    width = 1,
  } = useResizeObserverDebounced<HTMLDivElement>(1);
  const fontSize = getPlotFontSize(appearance.fontSize, height);

  // Additional constraints are applied for legends depending on panel width/height and if
  // view is single run
  const isLegendShown =
    legend != null &&
    getIsLegendShown({
      isMultiMetric: panel.isMultiMetric,
      isSingleRun: legend.isSingleRun,
      panelSize: {height, width},
      showLegend: legend.show,
    });
  const legendPosition = legend?.position ?? DEFAULT_GRAPH_LEGEND_POSITION;

  const numericLines = useMemo(() => resolveNumericSeries(series), [series]);
  const lines = useMemo(
    () =>
      presentChartSeries(numericLines, seriesStylesById, seriesMetadataById),
    [numericLines, seriesStylesById, seriesMetadataById]
  );
  const timestep = numericLines[0]?.timestep;

  const {filteredLines, filteredNumericLines} = useLinesForDisplay(
    numericLines,
    lines,
    {
      xScale: axis.xScale,
      yScale: axis.yScale,
      isFullFidelity: rendering.isFullFidelity,
      isHovered,
    }
  );
  const isRegexMetricSettling = useRegexMetricSettlingLoading(
    loading.regexSettlingKey,
    filteredNumericLines
  );
  const effectiveLoading = loading.isActive || isRegexMetricSettling;

  const {
    legendLines: numericLegendLines,
    visibleLines: numericVisibleLines,
    hiddenLines: numericHiddenLines,
  } = useMemo(
    function selectNumericLinesForDisplay() {
      return partitionLines(filteredNumericLines);
    },
    [filteredNumericLines]
  );
  const legendLines = presentNumericLines(numericLegendLines, filteredLines);
  const visibleLines = useMemo(
    () => presentNumericLines(numericVisibleLines, filteredLines),
    [numericVisibleLines, filteredLines]
  );
  const hiddenLines = presentNumericLines(numericHiddenLines, filteredLines);

  // derived values
  const [errorDetail, errorMessage] = getErrorMessage(
    errors,
    notices?.isDelayed === true,
    filteredNumericLines,
    zoom
  );

  const renderBarChart = isBarPlot(
    numericLines,
    zoom.isZooming,
    fallbacks?.singlePointStyle
  );
  const renderPanelError = !effectiveLoading && errorMessage != null;

  const [isDrawing, setIsDrawing] = useState(false);

  const panelErrorMetaInfo = {isFullFidelity: rendering.isFullFidelity};
  const warningPanel = {isMultiMetric: panel.isMultiMetric};
  const warningRendering = {isFullFidelity: rendering.isFullFidelity};

  const warning = limitNotices.displayRowLimitPerRunset !== undefined && (
    <LinePlotLegendWarningView
      notices={limitNotices}
      panel={warningPanel}
      rendering={warningRendering}
      loading={effectiveLoading}
      lines={legendLines}
      disallowSelect={isDrawing}
    />
  );

  if (
    renderBarChart ||
    renderPanelError ||
    (errors.hasUnexpectedError && !effectiveLoading)
  ) {
    return (
      <div
        className="chart"
        data-test={`runs-line-plot-error-${
          errors.savedXKey ?? 'unknown-x-axis'
        }`}
        onMouseEnter={function markErrorPanelHovered() {
          setIsHovered(true);
        }}
        onMouseLeave={function clearErrorPanelHovered() {
          setIsHovered(false);
        }}
        ref={elementRef}>
        {title !== undefined && (
          <PanelTitleView
            title={title.text}
            fontSize={fontSize}
            highlightSearch={title.renderHighlightedText}
            onTitleClick={title.onActivate}
          />
        )}
        {renderBarChart && warning}
        <div className="chart-content" data-test="chart-content">
          {renderPanelError ? (
            <PanelError
              panelViewType="Run History Line Plot"
              panelID={panel.id}
              metaInfo={panelErrorMetaInfo}
              message={
                errors.hasUnexpectedError ? (
                  <div>
                    An unexpected error was detected. Will try again shortly.
                  </div>
                ) : (
                  errorMessage
                )
              }
              logErrorMessage={
                errors.hasUnexpectedError
                  ? 'An unexpected error was detected. Will try again shortly.'
                  : typeof errorDetail === 'string'
                    ? errorDetail
                    : 'Chart error occurred'
              }
            />
          ) : (
            <BarChart
              lines={lines}
              min={fallbacks?.barYMin}
              max={fallbacks?.barYMax}
            />
          )}
        </div>
      </div>
    );
  }

  const issueStatus = getIssuesWrapperStatus({
    annotationActive: annotations?.isActive ?? false,
    issueCount: annotations?.activeCount ?? 0,
    issueRampStatus: annotations?.isEnabled ? 'enabled' : 'disabled',
  });

  return (
    <div
      data-x-axis={errors.savedXKey ?? 'unknown-x-axis'}
      data-test="chart-content"
      ref={elementRef}
      style={expandStyles}>
      <IconPositioningContextProvider issues={issueLines} width={width}>
        <PlotLayoutView
          delayed={notices?.isDelayed === true}
          DelayWarning={
            notices?.isDelayed && (
              <DelayedMetricsNotice notices={delayedNotices} />
            )
          }
          IssueMarkers={
            annotations && <IssueMarkerList annotations={annotations} />
          }
          issueStatus={issueStatus}
          fontSize={plotFontSizeToPx.legend[fontSize]}
          handleHover={setIsHovered}
          loading={effectiveLoading}
          numLines={
            linesHaveRenderableData(visibleLines) ? visibleLines.length : 0
          }
          Graph={
            isResizing ? (
              <div />
            ) : (
              <LinePlot
                excludeOutliers={axis.outlierPolicy}
                filteredLines={visibleLines}
                numericLines={numericVisibleLines}
                seriesStylesById={seriesStylesById}
                fontSize={fontSize}
                interaction={interaction}
                isDrawing={isDrawing}
                isHovered={isHovered}
                issueLines={issueLines}
                hoveredIssueSource={annotationHoverSource}
                isZoomedXMax={zoom.isZoomedXMax}
                isZoomedXMin={zoom.isZoomedXMin}
                panelId={panel.id}
                tooltip={tooltip}
                setIsDrawing={setIsDrawing}
                svg={rendering.exportMode}
                timestep={timestep}
                xAxis={prettyXAxisLabel(axis.xKey, numericLines)}
                xAxisTitle={axis.xTitle}
                xDomain={zoom.xDomain}
                xScale={axis.xScale}
                yAxis={''}
                yAxisTitle={axis.yTitle}
                yDomain={zoom.yDomain}
                yScale={axis.yScale}
                zoomCallback={zoom.onZoom}
                zooming={zoom.isZooming}>
                {
                  <MissingRunBanner
                    interactionSource={interaction.source}
                    lines={visibleLines}
                  />
                }
              </LinePlot>
            )
          }
          legendPosition={legendPosition}
          Legend={
            legendFeature && (
              <LinePlotLegendView
                legend={legendFeature}
                fontSize={fontSize}
                hiddenLines={hiddenLines}
                isMultiMetric={panel.isMultiMetric}
                lines={legendLines}
                isVertical={!['east', 'west'].includes(legendPosition)}
                disallowSelect={isDrawing}
              />
            )
          }
          showLegend={isLegendShown}
          sizeRef={layout.sizeRef}
          Title={
            title !== undefined && (
              <PanelTitleView
                title={title.text}
                highlightSearch={title.renderHighlightedText}
                onTitleClick={title.onActivate}
                fontSize={fontSize}
                disallowSelect={isDrawing}
              />
            )
          }
          Warning={warning}
        />
      </IconPositioningContextProvider>
      {samplingNotices.isSamplingFallback !== undefined && (
        <SamplingWarningView
          notices={samplingNotices}
          style={SAMPLING_WARNING_STYLE}
          width={width}
        />
      )}
    </div>
  );
};
const Graph = memo(GraphComp);
Graph.displayName = 'Graph';

// Readiness keeps the provider and renderer mounted only when host data is available.
const GraphWrapperComp: React.FC<GraphRenderProps> = function GraphWrapperComp(
  props
) {
  const {appearance, axis, loading, panel, rendering} = props;
  if (!loading.isReady) {
    return <p>No Histories</p>;
  }

  const settings: GraphRendererSettings = {
    isMultiMetric: panel.isMultiMetric,
    isDarkMode: appearance.isDarkMode,
    forceReactVis: rendering.forceReactVis,
    isFullFidelity: rendering.isFullFidelity,
    pointVisualizationMethod: rendering.pointStrategy,
    plotType: rendering.plotType,
    uplotEnabled: rendering.allowUPlot,
    xAxis: axis.xKey,
    xAxisIsDatetime: axis.xIsDatetime,
    xLogScale: axis.xScale === 'log',
    yLogScale: axis.yScale === 'log',
  };
  return (
    <LinePlotSettingsProvider settings={settings}>
      <Graph {...props} />
    </LinePlotSettingsProvider>
  );
};
export const GraphWrapper = memo(GraphWrapperComp);
GraphWrapper.displayName = 'GraphWrapper';
